import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {addQueryToPath} from '../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InlineLinkButton} from '../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {UseApiState, useFetchApi} from '../../../../../../srcCommon/helpers/hookHelpers';
import {ActivitySourceContactModel} from '../../../../../models/api/activitySourceModel';
import {ApiSuppressionLists, SuppressionListsModel} from '../../../../../models/api/suppressionListsModel';
import {deleteAsync} from '../../../../../services/http/http';
import {OkCancelDialog} from '../../../../common/dialogs/okCancelDialog';
import {ActivityWithIcon} from '../activityWithIcon/activityWithIcon';

/*
 * Props.
 */

interface BasicActivityErrorViewerProps {
  activityError?: string;
  activitySource: ActivitySourceContactModel;
  namespace: string;
  shouldShowSuppressionListCTA: boolean;
  companyUrl: string;
  onSuppressionListsRemoval: Handler;
  onDeliverabilityClick: Handler;
}

/*
 * Style.
 */

const StyledBannerDiv = styled.div`
  grid-area: banner;

  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'icon content';

  margin: 10px 0px 0px;
  padding: 8px;

  background-color: ${(props) => props.color};
  border-radius: 8px;
`;

const StyledIconDiv = styled.div`
  grid-area: icon;

  margin: 3px 4px;
`;

const StyledContentDiv = styled.div`
  grid-area: content;

  margin: 0px 6px 0px 6px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  line-height: 1.6;
`;

const StyledOverflowDiv = styled.div`
  max-width: 550px;

  & > div {
    ${ellipsis()};
  }
`;

/*
 * Component.
 */

type SuppressionListsUseState = UseApiState<SuppressionListsModel>;

export const BasicActivityErrorViewer: FC<BasicActivityErrorViewerProps> = (props) => {
  const theme = useUserTheme();
  const {
    activityError,
    activitySource,
    shouldShowSuppressionListCTA,
    companyUrl,
    onDeliverabilityClick,
    onSuppressionListsRemoval,
  } = props;

  const [suppressionLists, setSuppressionLists] = useState<SuppressionListsUseState>();

  const fetchSuppressionListApi = useFetchApi({
    apiType: ApiSuppressionLists,
    debounceDelay: 100,
    onStateChange: (result) => {
      if (result.asyncStatus !== AsyncStatusesEnum.LOADED) {
        return;
      }

      setSuppressionLists(result);
    },
  });

  // Fetch the suppression lists the email is on upon the first render.
  useInitialEffect(() => {
    if (!shouldShowSuppressionListCTA) {
      return;
    }

    const path = addQueryToPath(`${companyUrl}/channels/${activitySource.senderChannelId}/suppression_list`, {
      email: activitySource.contact.handle,
    });

    fetchSuppressionListApi(path);
  });

  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isOkDisabled, setIsOkDisabled] = useState(false);
  const [suppressionDeleteError, setSuppressionDeleteError] = useState();

  const openAlert = () => setIsDeleteAlertOpen(true);
  const onRequestClose = () => setIsDeleteAlertOpen(false);

  const onOkClick = async () => {
    // If there are any suppression lists, remove the email from those.
    if (suppressionLists && suppressionLists.result) {
      // The BE/Sendgrid doesn't have any way to batch remove from lists
      // so we need to perform multiple requests if applicable.
      try {
        await Promise.all(
          suppressionLists.result.map((l) =>
            deleteAsync(`${companyUrl}/channels/${activitySource.senderChannelId}/suppression_list`, {
              email: activitySource.name,
              list: l,
            }),
          ),
        );

        onSuppressionListsRemoval();
        setIsOkDisabled(true);
      } catch (e) {
        if (e.status === 403) {
          setSuppressionDeleteError(e.body?.message);
          setIsOkDisabled(true);

          return;
        }
      }
    }

    onRequestClose();
  };

  if (shouldShowSuppressionListCTA && suppressionLists?.asyncStatus !== AsyncStatusesEnum.LOADED) {
    return null;
  }

  return (
    <StyledOverflowDiv>
      <ActivityWithIcon iconName="warningActivity" iconColor={theme.palette.red.shade40}>
        <Trans id="activity-basic-sendbounceerror">
          A message <strong>failed to be delivered</strong>
        </Trans>{' '}
        {renderErrorOrCTA(activityError, suppressionLists, openAlert)}
        {renderDeleteAlert(
          activitySource.name,
          isDeleteAlertOpen,
          isOkDisabled,
          suppressionDeleteError,
          onDeliverabilityClick,
          onOkClick,
          onRequestClose,
        )}
      </ActivityWithIcon>
    </StyledOverflowDiv>
  );
};

function renderDeleteAlert(
  emailAddress: string,
  isAlertOpen: boolean,
  isOkDisabled: boolean,
  suppressionDeleteError: string | undefined,
  onDeliverabilityClick: Handler,
  onOkClick: Handler,
  onRequestClose: Handler,
) {
  if (!isAlertOpen) {
    return null;
  }

  return (
    <OkCancelDialog
      title={<Trans id="bounceerror-dialog-title">Remove address from suppression lists</Trans>}
      okButtonLabel={<Trans id="bounceerror-dialog-confirm">Remove</Trans>}
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onOkClick={onOkClick}
      isOkDisabled={isOkDisabled}
      onRequestClose={onRequestClose}
    >
      <Trans id="bounceerror-dialog-content">
        Removing <strong>{emailAddress}</strong> from all suppression lists will allow you to send messages to
        their email address again.
      </Trans>
      {renderInfoBanner(onDeliverabilityClick)}
      {renderDeleteErrorBanner(suppressionDeleteError)}
    </OkCancelDialog>
  );
}

function renderInfoBanner(onDeliverabilityClick: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledBannerDiv color={theme.palette.blue.shade20}>
          <StyledIconDiv>
            <Icon name="infoFilledSmall" color={theme.palette.blue.shade40} />
          </StyledIconDiv>
          <StyledContentDiv>
            <Trans id="suppress-banner-info">
              We recommend verifying your SPF/DKIM DNS{' '}
              <InlineLinkButton onClick={onDeliverabilityClick}>settings</InlineLinkButton> for this channel,
              as that might be why this email was flagged for the suppression list.
            </Trans>
          </StyledContentDiv>
        </StyledBannerDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderDeleteErrorBanner(suppressionDeleteError: string | undefined) {
  if (!suppressionDeleteError) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledBannerDiv color={theme.palette.red.shade20}>
          <StyledIconDiv>
            <Icon name="warningActivity" color={theme.palette.red.shade40} />
          </StyledIconDiv>
          <StyledContentDiv>
            <Trans id="suppress-delete-error">{suppressionDeleteError}</Trans>
          </StyledContentDiv>
        </StyledBannerDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderErrorOrCTA(
  activityError: string | undefined,
  suppressionLists: SuppressionListsUseState | undefined,
  onClick: Handler,
) {
  if (!suppressionLists || !suppressionLists.result || suppressionLists.result.length < 1) {
    return activityError ? <Trans id="bounceerror-info">({activityError})</Trans> : null;
  }

  return (
    <>
      <Trans id="bounceerror-suppression-info">
        because <strong>the address is in a suppression list</strong>
      </Trans>{' '}
      <InlineLinkButton onClick={onClick}>
        <Trans id="bounceerror-cta">Remove</Trans>
      </InlineLinkButton>
    </>
  );
}
